import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';

import Button from '~/components/core/Atomic/Buttons/Button';
import MenuItem from '~/components/core/Atomic/MenuItem';
import Tooltip from '~/components/core/Atomic/Tooltip';
import { usePaymentsConfiguration } from '~/components/hooks/usePaymentsConfiguration';
import { useExposureSubReserves } from '~/components/hooks/useSubReserves';
import useDataFetcher from '~/components/useDataFetcher';
import { getLocalDateTime } from '~/DateTimeUtils';
import { reportAxiosError } from '~/Utils';
import { getPolicyCurrency, mergeWithPolicyCurrency } from '~/Utils/currencyUtils';

import { CurrencyFormatterContextProvider } from '../../../CurrencyFormatterContext';
import { useCms } from '../../../hooks/useCms';
import { DatePickerTextFieldFormik, MonetaryValueTextFieldFormik, TextFieldFormik } from '../../../TextFieldFormik';
import { SubReservePaymentAmountsFormik } from '../../SubReservesFormik';

import CurrencyConversionDisplay from './CurrencyConversionDisplay';

import { useStyles } from '../../../../assets/styles';

const PaymentAmountFields = ({
  readOnly,
  payableType,
  payableWithReserve,
  exposure,
  claim,
  isLoadingExchangeRates,
  setIsLoadingExchangeRates,
}) => {
  const { values, isSubmitting, setFieldValue } = useFormikContext();
  const { isPrefillReserveAmountsCapabilityEnabled, paymentConfigurationCurrencies, isMultiCurrencyEnabled } =
    usePaymentsConfiguration();
  const { isMultipleSubReservePaymentsEnabled, isSubReservesConfigEnabled } = useExposureSubReserves(
    payableType,
    exposure
  );
  const { classes } = useStyles();
  const { userOrganization } = useCms();

  const [lastFetchedDate, setLastFetchedDate] = useState(null);

  const reserveAmount = payableWithReserve.reserve;
  const isReserveZero = reserveAmount === 0;
  const handlePreFill = () => {
    setFieldValue('amount', reserveAmount);
    if (isMultipleSubReservePaymentsEnabled) {
      const subReserves = payableWithReserve.sub_reserves;
      const nonZeroSubReservesList = Object.keys(payableWithReserve.sub_reserves)
        .filter((subReserveKey) => subReserves[subReserveKey] !== 0)
        .map((subReserveKey) => ({ type_key: subReserveKey, amount: subReserves[subReserveKey] }));

      setFieldValue('sub_reserve_amounts', nonZeroSubReservesList);
    }
  };

  // Prepare parameters for exchange rates API call
  const currentDate = getLocalDateTime(values.conversion_date || new Date());
  const selectedCurrency = values.request_currency;

  const policyCurrency = getPolicyCurrency(claim);

  const availableCurrencies = isMultiCurrencyEnabled
    ? mergeWithPolicyCurrency(paymentConfigurationCurrencies, policyCurrency)
    : null;

  const needsApiCall =
    isMultiCurrencyEnabled &&
    currentDate &&
    values.request_currency !== policyCurrency &&
    lastFetchedDate !== currentDate;

  const currencies =
    isMultiCurrencyEnabled && paymentConfigurationCurrencies?.length > 0
      ? paymentConfigurationCurrencies.join(',')
      : null;

  const {
    isLoading: isLoadingRates,
    isError: isErrorRates,
    data: exchangeRatesData,
  } = useDataFetcher(
    `/api/v1/currency_conversion/organizations/${userOrganization.id}/exchange_rates`,
    {
      params: {
        date: currentDate,
        currencies,
        base_currency: policyCurrency,
      },
    },
    { shouldFetch: isMultiCurrencyEnabled && currentDate && currencies }
  );

  useEffect(() => {
    setIsLoadingExchangeRates(isLoadingRates);
  }, [isLoadingRates, setIsLoadingExchangeRates]);

  // Handle exchange rates data and update formik values
  useEffect(() => {
    if (!isMultiCurrencyEnabled) return;

    // Handle case where user selects policy currency
    if (values.request_currency === policyCurrency) {
      setFieldValue('conversion_rate', undefined);
      setFieldValue('conversion_extra', undefined);
      return;
    }

    // Handle API error
    if (isErrorRates && needsApiCall) {
      setFieldValue('conversion_rate', undefined);
      setFieldValue('conversion_extra', undefined);
      setFieldValue('conversion_date', undefined);
      return;
    }

    // Handle successful API response
    if (exchangeRatesData && needsApiCall && values.request_currency) {
      const targetCurrency = values.request_currency;
      const rate = exchangeRatesData.rates[targetCurrency];

      if (!rate) {
        reportAxiosError(new Error(`No exchange rate found for ${targetCurrency}`));
        return;
      }

      setFieldValue('conversion_rate', rate);
      setFieldValue('conversion_extra', {
        api_response: exchangeRatesData.rates,
        base_currency: exchangeRatesData.base_currency,
        target_currency: targetCurrency,
        api_source: exchangeRatesData.api_source,
        fetched_at: exchangeRatesData.fetched_at,
        date_requested: exchangeRatesData.date,
      });

      setLastFetchedDate(currentDate);
      return;
    }

    // Handle cached data when currency changes but date hasn't
    if (!needsApiCall && exchangeRatesData?.rates && values.request_currency) {
      const rate = exchangeRatesData.rates[values.request_currency];
      if (!rate) {
        reportAxiosError(new Error(`No exchange rate found for ${values.request_currency}`));
        return;
      }

      setFieldValue('conversion_rate', rate);
    }
  }, [
    isMultiCurrencyEnabled,
    values.request_currency,
    policyCurrency,
    exchangeRatesData,
    isErrorRates,
    needsApiCall,
    currentDate,
    setFieldValue,
    setLastFetchedDate,
  ]);

  const shouldDisplayPreFill =
    isPrefillReserveAmountsCapabilityEnabled && (!isSubReservesConfigEnabled || isMultipleSubReservePaymentsEnabled);

  return (
    <>
      {shouldDisplayPreFill ? (
        <div className="pt-8">
          <Tooltip title="Current reserve amount is 0" disabled={!isReserveZero} placement="right-start" arrow>
            <span>
              <Button onClick={handlePreFill} variant="outlined" color="primary" disabled={isReserveZero}>
                Pre-fill {isMultipleSubReservePaymentsEnabled ? 'sub-reserves amounts' : 'reserve amount'}
              </Button>
            </span>
          </Tooltip>
        </div>
      ) : null}
      {isSubReservesConfigEnabled && !isMultipleSubReservePaymentsEnabled ? null : (
        <div className="flex flex-row items-start gap-12">
          {isMultiCurrencyEnabled ? (
            <>
              <div className="w-[80px]">
                <TextFieldFormik
                  id="request_currency"
                  label="Currency"
                  className={classes.textField}
                  fullWidth
                  select
                  disabled={readOnly}
                >
                  {availableCurrencies.map((currency) => (
                    <MenuItem key={currency} value={currency}>
                      {currency}
                    </MenuItem>
                  ))}
                </TextFieldFormik>
              </div>
              {selectedCurrency !== policyCurrency ? (
                <div className="w-[180px]">
                  <DatePickerTextFieldFormik
                    id="conversion_date"
                    label="Currency Conversion Date"
                    className={classes.textField}
                    disabled={readOnly || isLoadingExchangeRates}
                    fullWidth
                    disableFuture
                    helperText={isLoadingExchangeRates ? 'Loading exchange rates...' : ''}
                  />
                </div>
              ) : null}
            </>
          ) : null}
          <CurrencyFormatterContextProvider currency={values.request_currency}>
            <MonetaryValueTextFieldFormik
              id="amount"
              label="Amount"
              className={classes.textField}
              disabled={readOnly}
              showOnly={isMultipleSubReservePaymentsEnabled}
            />
          </CurrencyFormatterContextProvider>
        </div>
      )}
      <div className="pt-8">
        <CurrencyConversionDisplay
          amount={values.amount}
          conversionRate={values.conversion_rate}
          conversionDate={values.conversion_date}
          doneDisplayingConversionDate={true}
          requestCurrency={values.request_currency}
          claim={claim}
        />
      </div>
      <div className="w-1/2">
        <SubReservePaymentAmountsFormik
          showOnly={readOnly}
          isSubmitting={isSubmitting}
          payableType={payableType}
          exposure={exposure}
          payableWithReserve={payableWithReserve}
          requestCurrency={values.request_currency}
          conversionRate={values.conversion_rate}
          policyCurrency={policyCurrency}
        />
      </div>
    </>
  );
};

PaymentAmountFields.propTypes = {
  readOnly: PropTypes.bool,
  payableType: PropTypes.string.isRequired,
  payableWithReserve: PropTypes.object.isRequired,
  exposure: PropTypes.object,
  claim: PropTypes.object,
  isLoadingExchangeRates: PropTypes.bool.isRequired,
  setIsLoadingExchangeRates: PropTypes.func.isRequired,
};

export default PaymentAmountFields;
